import * as React from "react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ToggleButton from "../commons/components/buttons/ToggleButton";
import TelInput from "../commons/components/form/input/TelInput";
import sfetch from "../commons/utils/super-fetch";
import ModalConfirm from "../commons/components/modal/ModalConfirm";
import Country from "../commons/model/address/Country";
import SavePanel from "./SavePanel";
import LoadingSpinner from "../flow/utils/LoadingSpinner";

export interface ReportNotificationSetting {
  reportReadyNotificationByEmail: boolean;
  reportReadyNotificationBySms: boolean;
  reportIncompleteNotificationByEmail: boolean;
  reportIncompleteNotificationBySms: boolean;
  phoneNumber?: string;
  changeUrl: string;
  selectedCountry: Country;
  smsTestUrl: string;
  smsSender?: string;
  countries: Country[];
}

interface ReportNotificationSettingPanelContainerProps extends ReportNotificationSetting {
  displayError: (message: string) => any;
}

interface ReportNotificationSettingState {
  reportReadyByEmail: boolean;
  reportReadyBySms: boolean;
  reportIncompleteByEmail: boolean;
  reportIncompleteBySms: boolean;
  phoneNumber: string;
  displayPhoneNumberConfirmationModal: boolean;
}

interface ReportNotificationSettingPanelProps {
  setState: Dispatch<SetStateAction<ReportNotificationSettingState>>;
  state?: ReportNotificationSettingState;
  sendNewSettings: () => Promise<any>;
  save: () => Promise<any>;
  refreshPhoneInputKey: number;
  cancel: () => void;
  savePanelDisabled: boolean;
  validForm: boolean;
  initialPhoneNumber?: string;
  selectedCountry: Country;
  countries: Country[];
}

export default function ReportNotificationSettingPanelContainer(
  props: ReportNotificationSettingPanelContainerProps
) {
  const [persistedState, setPersistedState] = useState<ReportNotificationSettingState>();
  const [updatedState, setUpdatedState] = useState<ReportNotificationSettingState>();
  const [savePanelDisabled, setSavePanelDisabled] = useState(true);
  const [validForm, setValidForm] = useState(true);

  const [refreshPhoneInputKey, setRefreshPhoneInputKey] = useState<number>(0);

  const [initialPhoneNumber, setInitialPhoneNumber] = useState<string>("");

  useEffect(() => {
    setPersistedState({
      reportReadyByEmail: props.reportReadyNotificationByEmail,
      reportReadyBySms: props.reportReadyNotificationBySms,
      reportIncompleteByEmail: props.reportIncompleteNotificationByEmail,
      reportIncompleteBySms: props.reportIncompleteNotificationBySms,
      phoneNumber: props.phoneNumber || "",
      displayPhoneNumberConfirmationModal: false,
    });
    setUpdatedState({
      reportReadyByEmail: props.reportReadyNotificationByEmail,
      reportReadyBySms: props.reportReadyNotificationBySms,
      reportIncompleteByEmail: props.reportIncompleteNotificationByEmail,
      reportIncompleteBySms: props.reportIncompleteNotificationBySms,
      phoneNumber: props.phoneNumber || "",
      displayPhoneNumberConfirmationModal: false,
    });
    setInitialPhoneNumber(props.phoneNumber || "");
  }, []);

  useEffect(() => {
    if (persistedState === undefined) {
      setSavePanelDisabled(true);
    } else {
      setSavePanelDisabled(
        persistedState.reportIncompleteByEmail == updatedState.reportIncompleteByEmail &&
          persistedState.reportIncompleteBySms == updatedState.reportIncompleteBySms &&
          persistedState.reportReadyByEmail == updatedState.reportReadyByEmail &&
          persistedState.reportReadyBySms == updatedState.reportReadyBySms &&
          persistedState.phoneNumber == updatedState.phoneNumber
      );
    }
    if (updatedState === undefined) {
      setValidForm(true);
    } else {
      setValidForm(
        !(
          (updatedState.reportIncompleteBySms || updatedState.reportReadyBySms) &&
          !updatedState.phoneNumber
        )
      );
    }
  }, [updatedState]);

  const getPhoneNumberFromInput = (): string => {
    if (
      (!updatedState.reportIncompleteBySms && !updatedState.reportReadyBySms) ||
      !updatedState.phoneNumber
    ) {
      return null;
    } else {
      return removeSpaces(updatedState.phoneNumber);
    }
  };

  const removeSpaces = (str): string => {
    return str.replace(/\s+/g, "");
  };

  const sendTestSms = (): Promise<any> => {
    let phoneNumber = getPhoneNumberFromInput();
    return sfetch(props.smsTestUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phoneNumber: phoneNumber,
        sender: props.smsSender,
      }),
    })
      .then(() =>
        setUpdatedState({
          ...updatedState,
          displayPhoneNumberConfirmationModal: true,
        })
      )
      .catch(() => props.displayError(i18n["Global.Status.technical_error"]));
  };

  const sendNewSettings = (): Promise<any> => {
    let phoneNumber = getPhoneNumberFromInput();
    return sfetch(props.changeUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reportReadyByEmail: updatedState.reportReadyByEmail,
        reportReadyBySms: updatedState.reportReadyBySms,
        reportIncompleteByEmail: updatedState.reportIncompleteByEmail,
        reportIncompleteBySms: updatedState.reportIncompleteBySms,
        phoneNumber: phoneNumber,
      }),
    })
      .then(() => {
        setUpdatedState({
          ...updatedState,
          displayPhoneNumberConfirmationModal: false,
        });
        setPersistedState({
          ...updatedState,
          displayPhoneNumberConfirmationModal: false,
        });
      })
      .catch(() => {
        setUpdatedState({
          ...updatedState,
          displayPhoneNumberConfirmationModal: false,
        });
        props.displayError(i18n["Global.Status.technical_error"]);
      });
  };

  const save = (): Promise<any> => {
    if (
      (updatedState.reportIncompleteBySms || updatedState.reportReadyBySms) &&
      updatedState.phoneNumber != persistedState.phoneNumber
    ) {
      return sendTestSms();
    } else {
      return sendNewSettings();
    }
  };

  const cancel = () => {
    setRefreshPhoneInputKey(refreshPhoneInputKey + 1);
    setUpdatedState(persistedState);
  };

  return (
    <ReportNotificationSettingPanel
      state={updatedState}
      setState={setUpdatedState}
      sendNewSettings={sendNewSettings}
      save={save}
      refreshPhoneInputKey={refreshPhoneInputKey}
      cancel={cancel}
      savePanelDisabled={savePanelDisabled}
      validForm={validForm}
      initialPhoneNumber={initialPhoneNumber}
      selectedCountry={props.selectedCountry}
      countries={props.countries}
    />
  );
}

function ReportNotificationSettingPanel(props: ReportNotificationSettingPanelProps) {
  if (props.state === undefined) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {props.state.displayPhoneNumberConfirmationModal && (
        <ModalConfirm
          cancel={() =>
            props.setState({
              ...props.state,
              displayPhoneNumberConfirmationModal: false,
            })
          }
          action={props.sendNewSettings}
          title={i18n["Page.Settings.Customisation.ReportReady.PhoneConfirmationModal.Title"]}
          actionButtonLabel={i18n["Global.Yes"]}
          cancelButtonLabel={i18n["Global.No"]}
        >
          <p>{i18n["Page.Settings.Customisation.ReportReady.PhoneConfirmationModal.Content"]}</p>
        </ModalConfirm>
      )}
      <div className="vertical-container">
        <p>{i18n["Page.Settings.Customisation.Notification.Report.Description"]}</p>
        <div className="setting-grid">
          <div className="item-highlight">
            {i18n["Page.Settings.Customisation.ReportIncomplete.Option.Description"]}
          </div>
          <span className="setting-description">
            <div className="notification-options-form">
              <ToggleButton
                id="toggle-button-report-incomplete-email"
                value={props.state.reportIncompleteByEmail}
                label={i18n["Page.Settings.Customisation.ReportIncomplete.Choice.Email"]}
                onChange={(newValue) => {
                  props.setState({
                    ...props.state,
                    reportIncompleteByEmail: newValue,
                  });
                }}
              />
              <ToggleButton
                id="toggle-button-report-incomplete-sms"
                value={props.state.reportIncompleteBySms}
                label={i18n["Page.Settings.Customisation.ReportIncomplete.Choice.Sms"]}
                onChange={(newValue) => {
                  props.setState({
                    ...props.state,
                    reportIncompleteBySms: newValue,
                    phoneNumber: newValue ? props.initialPhoneNumber : "",
                  });
                }}
              />
            </div>
          </span>
        </div>
        <div className="setting-grid">
          <div className="item-highlight">
            {i18n["Page.Settings.Customisation.ReportReady.Option.Description"]}
          </div>
          <span className="setting-description">
            <div className="notification-options-form">
              <ToggleButton
                id="toggle-button-report-ready-email"
                value={props.state.reportReadyByEmail}
                label={i18n["Page.Settings.Customisation.ReportReady.Choice.Email"]}
                onChange={(newValue) => {
                  props.setState({
                    ...props.state,
                    reportReadyByEmail: newValue,
                  });
                }}
              />
              <ToggleButton
                id="toggle-button-report-ready-sms"
                value={props.state.reportReadyBySms}
                label={i18n["Page.Settings.Customisation.ReportReady.Choice.Sms"]}
                onChange={(newValue) => {
                  props.setState({
                    ...props.state,
                    reportReadyBySms: newValue,
                    phoneNumber: newValue ? props.initialPhoneNumber : "",
                  });
                }}
              />
            </div>
          </span>
        </div>
        {(props.state.reportIncompleteBySms || props.state.reportReadyBySms) && (
          <div key={props.refreshPhoneInputKey} className="setting-grid notification-phone-number">
            <div className="item-highlight">{i18n["Form.Field.Title.PhoneNumber"]}</div>
            <div className="h-flex">
              <TelInput
                name="phoneNumber"
                placeholder={i18n["Form.BookAnAppointment.Phone.Placeholder"]}
                initialValue={props.initialPhoneNumber}
                selectedCountry={props.selectedCountry}
                availableCountries={props.countries}
                onChange={(input) => {
                  props.setState({
                    ...props.state,
                    phoneNumber: input.value,
                  });
                }}
              />
            </div>
          </div>
        )}
        <div className="h-flex justify-end">
          <SavePanel
            onSave={props.save}
            onCancel={props.cancel}
            disabled={props.savePanelDisabled}
            isValid={props.validForm}
          />
        </div>
      </div>
    </>
  );
}
